import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ImageUpload } from '../components/ImageUpload';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Sparkles, Infinity as InfinityIcon, TrendingUp, Award, CheckCircle2, Plus, Trash2,
  Users, LayoutGrid, BarChart3, UserCircle, Compass, Handshake, Star, ArrowRight, Clock,
} from 'lucide-react';

const PLATFORM_OPTIONS = ['Instagram', 'YouTube', 'TikTok', 'Facebook', 'X / Twitter', 'Blog / Website', 'Pinterest', 'Other'];
const BUDGET_SEGMENTS = ['Luxury', 'Mid-range', 'Budget', 'Backpacking', 'Adventure', 'Family', 'Solo'];
const AGE_RANGES = ['13-17', '18-24', '25-34', '35-44', '45-54', '55+'];
const CONTENT_FORMATS = ['Reels / Shorts', 'Long-form video', 'Blog articles', 'Photography', 'Stories', 'Live streams', 'Newsletters'];

const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-muted text-foreground' },
  submitted: { label: 'Submitted', cls: 'bg-blue-500/10 text-blue-600' },
  under_review: { label: 'Under Review', cls: 'bg-amber-500/10 text-amber-600' },
  changes_requested: { label: 'Changes Requested', cls: 'bg-orange-500/10 text-orange-600' },
  approved: { label: 'Approved', cls: 'bg-accent/15 text-accent' },
  rejected: { label: 'Not Approved', cls: 'bg-red-500/10 text-red-600' },
  suspended: { label: 'Suspended', cls: 'bg-red-500/10 text-red-600' },
  pending: { label: 'Pending', cls: 'bg-amber-500/10 text-amber-600' },
};

const HOW_IT_WORKS = [
  ['Apply', 'Tell us about your platforms and audience.'],
  ['Get verified', 'Our team reviews and verifies your profile.'],
  ['Create itineraries', 'Build premium day-by-day travel guides.'],
  ['Submit for review', 'We check quality before publishing.'],
  ['Get published', 'Your itineraries go live on FlightDeals.in.'],
  ['Share & earn', 'Drive bookings and earn from conversions.'],
];

const FAQS = [
  ['Who can apply?', 'Any travel creator, influencer, blogger, photographer or publisher with an engaged audience.'],
  ['Can YouTubers apply?', 'Absolutely. Add your channel URL and subscriber count during the application.'],
  ['Can bloggers apply?', 'Yes — add your website, monthly traffic and primary audience location.'],
  ['Can Instagram creators apply?', 'Yes. Add your handle, followers and average reel views.'],
  ['Do I need a minimum follower count?', 'There is no hard minimum. We look at engagement and content quality too.'],
  ['How do affiliate commissions work?', 'You earn a configurable share of confirmed commissions from bookings made through your itineraries.'],
  ['What are paid campaigns?', 'Brand-sponsored collaborations where you receive a fixed fee for creating content or itineraries.'],
  ['What are barter campaigns?', 'Collaborations where you receive stays, experiences or products instead of (or with) a cash fee.'],
  ['How are payouts handled?', 'Earnings accrue in your creator ledger. Payouts are processed per the agreed terms.'],
  ['How long does approval take?', 'Most applications are reviewed within a few business days.'],
  ['Can I create unlimited itineraries?', 'Yes — create as many as you like, subject to our quality moderation.'],
];

const BENEFITS = [
  [InfinityIcon, 'Unlimited itineraries', 'Build as many premium guides as you like.'],
  [TrendingUp, 'Earn affiliate revenue', 'Get a configurable share of confirmed commissions.'],
  [Award, 'Paid & barter campaigns', 'Work with FlightDeals.in and travel brands.'],
  [BarChart3, 'Creator analytics', 'Track views, clicks, conversions & earnings.'],
  [UserCircle, 'Dedicated Creator Studio', 'A pro workspace to manage everything.'],
  [Star, 'Featured opportunities', 'Get discovered in our creator marketplace.'],
];

const emptyPlatform = () => ({ platform: 'Instagram', url: '', handle: '', followers: '', avg_views: '', engagement_rate: '', niche: '' });

export default function CreatorJoin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [creator, setCreator] = useState(null);
  const [options, setOptions] = useState({ creator_types: [], collab_models: [] });
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', display_name: '', username: '', phone: '', country: '', city: '', bio: '',
    profile_photo: '', creator_type: [], instagram: '', niche: '',
    audience_primary_country: '', audience_secondary: '', age_range: '', languages: '',
    travel_interests: '', budget_segment: '', content_formats: [], niches: '', destinations: '',
    years_creating: '', previous_collabs: '', portfolio: '', media_kit: '',
    commercial_models: [], expected_fee_itinerary: '', expected_campaign_fee: '', preferred_model: '',
    accurate: false, agree_terms: false, why_join: '',
  });
  const [platforms, setPlatforms] = useState([emptyPlatform()]);

  useEffect(() => { document.title = 'Become a Creator | FlightDeals.in'; }, []);
  useEffect(() => { api.creatorApplyOptions().then(({ data }) => setOptions(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (user) {
      api.creatorMe().then(({ data }) => {
        if (data.is_creator) { setCreator(data.creator); prefill(data.creator); }
        setForm((f) => ({ ...f, name: f.name || user.name || '', display_name: f.display_name || user.name || '' }));
      }).catch(() => {});
    }
  }, [user]);

  const prefill = (c) => {
    setForm((f) => ({
      ...f, name: c.name || '', display_name: c.display_name || c.name || '', phone: c.phone || '',
      country: c.country || '', city: c.city || '', bio: c.bio || '', profile_photo: c.profile_photo || '',
      creator_type: c.creator_type || [], niche: c.niche || '',
      niches: (c.niches || []).join(', '), languages: (c.languages || []).join(', '),
      destinations: (c.destinations || []).join(', '), content_formats: c.content_formats || [],
      years_creating: c.years_creating || '', previous_collabs: c.previous_collabs || '',
      portfolio: c.portfolio || '', media_kit: (c.media_kit || [])[0] || '',
      audience_primary_country: c.audience?.primary_country || '',
      audience_secondary: (c.audience?.secondary_countries || []).join(', '),
      age_range: c.audience?.age_range || '', travel_interests: (c.audience?.interests || []).join(', '),
      budget_segment: c.audience?.budget_segment || '',
      commercial_models: c.commercial?.models || [], expected_fee_itinerary: c.commercial?.expected_fee_itinerary || '',
      expected_campaign_fee: c.commercial?.expected_campaign_fee || '', preferred_model: c.commercial?.preferred_model || '',
      why_join: c.why_join || '', username: c.username || '',
    }));
    if ((c.platforms || []).length) setPlatforms(c.platforms.map((p) => ({ ...emptyPlatform(), ...p })));
  };

  const toggleArr = (key, val) => setForm((f) => ({
    ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val],
  }));

  const setPlatform = (i, key, val) => setPlatforms((ps) => ps.map((p, idx) => idx === i ? { ...p, [key]: val } : p));
  const addPlatform = () => setPlatforms((ps) => [...ps, emptyPlatform()]);
  const removePlatform = (i) => setPlatforms((ps) => ps.filter((_, idx) => idx !== i));

  const csv = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

  const buildPayload = () => ({
    name: form.name, username: form.username || form.display_name || form.name,
    display_name: form.display_name, phone: form.phone, country: form.country, city: form.city,
    bio: form.bio, profile_photo: form.profile_photo, creator_type: form.creator_type,
    platforms: platforms.filter((p) => p.platform && (p.url || p.handle || p.followers)).map((p) => ({
      platform: p.platform, url: p.url, handle: p.handle,
      followers: parseInt(p.followers || 0, 10) || 0, avg_views: parseInt(p.avg_views || 0, 10) || 0,
      engagement_rate: p.engagement_rate ? parseFloat(p.engagement_rate) : null, niche: p.niche,
    })),
    niche: form.niche, niches: csv(form.niches), languages: csv(form.languages),
    destinations: csv(form.destinations), content_formats: form.content_formats,
    years_creating: form.years_creating ? parseInt(form.years_creating, 10) : null,
    previous_collabs: form.previous_collabs, portfolio: form.portfolio,
    media_kit: form.media_kit ? [form.media_kit] : [],
    audience: {
      primary_country: form.audience_primary_country, secondary_countries: csv(form.audience_secondary),
      age_range: form.age_range, interests: csv(form.travel_interests), budget_segment: form.budget_segment,
    },
    commercial: {
      models: form.commercial_models, expected_fee_itinerary: form.expected_fee_itinerary,
      expected_campaign_fee: form.expected_campaign_fee, preferred_model: form.preferred_model,
    },
    accurate: form.accurate, agree_terms: form.agree_terms, why_join: form.why_join,
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }
    if (!form.accurate || !form.agree_terms) { toast.error('Please confirm accuracy and agree to the terms.'); return; }
    if (!form.creator_type.length) { toast.error('Select at least one creator type.'); return; }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const isResubmit = creator && !['approved'].includes(creator.approval_status);
      const { data } = isResubmit ? await api.creatorResubmit(payload) : await api.creatorApply(payload);
      setCreator(data.creator);
      setShowForm(false);
      toast.success(isResubmit ? 'Application resubmitted!' : 'Application submitted!');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Could not submit'); }
    finally { setSubmitting(false); }
  };

  const status = creator?.approval_status;
  const canResubmit = creator && ['changes_requested', 'rejected', 'draft'].includes(status);
  const sm = STATUS_META[status] || STATUS_META.pending;

  const scrollToApply = () => {
    if (creator && !canResubmit) return;
    setShowForm(true);
    setTimeout(() => document.getElementById('apply-section')?.scrollIntoView({ behavior: 'smooth' }), 60);
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 h-[420px]">
          <img src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1600&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/85 via-primary/80 to-background" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-primary-foreground">
          <span className="inline-flex items-center gap-1.5 text-accent text-sm font-semibold bg-accent/15 px-3 py-1 rounded-full"><Sparkles className="h-4 w-4" /> Creator Partnership Program</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-semibold max-w-3xl leading-tight">Turn Your Travel Content Into Your Next Revenue Stream.</h1>
          <p className="mt-4 text-primary-foreground/85 max-w-2xl text-lg">Create inspiring travel itineraries for your audience on FlightDeals.in, connect them with flights, hotels and experiences, and earn through qualified bookings.</p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Button onClick={scrollToApply} className="rounded-xl h-12 px-6 bg-accent text-accent-foreground hover:bg-accent/90 text-base" data-testid="hero-apply-cta">
              {canResubmit ? 'Update & Resubmit' : 'Apply as a Creator'} <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <Button onClick={() => navigate('/creators/itineraries')} variant="outline" className="rounded-xl h-12 px-6 bg-background/10 border-primary-foreground/30 text-primary-foreground hover:bg-background/20 text-base" data-testid="hero-explore-cta">
              Explore Creator Itineraries
            </Button>
          </div>
        </div>
      </section>

      {/* Status card (if applied) */}
      {creator && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 relative">
          <Card className="p-5 rounded-2xl flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid="creator-status-card">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-accent" />
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">Your application</p>
                  <Badge className={`rounded-full ${sm.cls}`} data-testid="application-status-badge">{sm.label}</Badge>
                </div>
                {creator.admin_message && <p className="text-sm text-muted-foreground mt-0.5" data-testid="admin-message">{creator.admin_message}</p>}
              </div>
            </div>
            <div className="flex gap-2">
              {status === 'approved' && <Button onClick={() => navigate('/creator/dashboard')} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="go-to-dashboard">Go to Creator Studio</Button>}
              {canResubmit && <Button onClick={scrollToApply} className="rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="resubmit-btn">Update & Resubmit</Button>}
            </div>
          </Card>
        </section>
      )}

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl font-semibold">Why join FlightDeals.in?</h2>
        <p className="text-muted-foreground mt-1">A professional program built for serious travel creators.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {BENEFITS.map(([Ic, t, s]) => (
            <Card key={t} className="p-5 rounded-2xl">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/10 text-accent mb-3"><Ic className="h-5 w-5" /></span>
              <p className="font-semibold">{t}</p>
              <p className="text-sm text-muted-foreground mt-1">{s}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-muted/40 border-y border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="font-display text-2xl font-semibold">How the program works</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {HOW_IT_WORKS.map(([t, s], i) => (
              <div key={t} className="flex items-start gap-3">
                <span className="grid place-items-center h-8 w-8 rounded-full bg-accent text-accent-foreground text-sm font-semibold shrink-0">{i + 1}</span>
                <div><p className="font-semibold text-sm">{t}</p><p className="text-sm text-muted-foreground">{s}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application form */}
      <section id="apply-section" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        {creator && !canResubmit && status !== 'approved' ? (
          <Card className="p-8 rounded-2xl text-center">
            <Clock className="h-10 w-10 mx-auto text-accent mb-2" />
            <p className="font-semibold">Application {sm.label.toLowerCase()}</p>
            <p className="text-sm text-muted-foreground mt-1">We'll notify you as your application progresses.</p>
          </Card>
        ) : creator && status === 'approved' ? (
          <Card className="p-8 rounded-2xl text-center">
            <Award className="h-10 w-10 mx-auto text-accent mb-2" />
            <p className="font-semibold">You're an approved creator!</p>
            <Button onClick={() => navigate('/creator/dashboard')} className="mt-4 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90">Open Creator Studio</Button>
          </Card>
        ) : (!showForm && !creator) ? (
          <div className="text-center">
            <h2 className="font-display text-2xl font-semibold">Ready to apply?</h2>
            <p className="text-muted-foreground mt-1">It takes about 5 minutes.</p>
            <Button onClick={scrollToApply} className="mt-5 rounded-xl h-12 px-6 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="apply-open-form">Apply as a Creator</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6" data-testid="creator-apply-form">
            <div>
              <h2 className="font-display text-2xl font-semibold">{canResubmit ? 'Update your application' : 'Creator application'}</h2>
              {!user && <p className="text-sm text-amber-600 mt-1">You'll be asked to sign in first.</p>}
            </div>

            {/* A. Basic */}
            <Card className="p-6 rounded-2xl space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><UserCircle className="h-4 w-4 text-accent" /> Basic information</h3>
              <ImageUpload variant="avatar" value={form.profile_photo} onChange={(url) => setForm({ ...form, profile_photo: url })} initials={(form.name || 'C').slice(0, 1).toUpperCase()} testId="profile-photo" />
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Full name *</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="apply-name" /></div>
                <div><Label>Creator / brand name</Label><Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} data-testid="apply-display-name" /></div>
                <div><Label>Username *</Label><Input required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="yourhandle" data-testid="apply-username" disabled={!!creator} /></div>
                <div><Label>Email</Label><Input value={user?.email || ''} disabled data-testid="apply-email" /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="apply-phone" /></div>
                <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="apply-country" /></div>
                <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="apply-city" /></div>
              </div>
              <div><Label>Short bio</Label><Textarea rows={2} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="apply-bio" /></div>
            </Card>

            {/* B. Creator type */}
            <Card className="p-6 rounded-2xl space-y-3">
              <h3 className="font-semibold flex items-center gap-2"><Compass className="h-4 w-4 text-accent" /> Creator type *</h3>
              <div className="flex flex-wrap gap-2">
                {(options.creator_types || []).map((t) => {
                  const active = form.creator_type.includes(t);
                  return (
                    <button type="button" key={t} onClick={() => toggleArr('creator_type', t)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${active ? 'bg-accent text-accent-foreground border-accent' : 'bg-background border-border hover:border-accent/50'}`}
                      data-testid={`creator-type-${t.split(' ')[0].toLowerCase()}`}>{t}</button>
                  );
                })}
              </div>
            </Card>

            {/* C. Social profiles */}
            <Card className="p-6 rounded-2xl space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-accent" /> Social profiles</h3>
                <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={addPlatform} data-testid="add-platform"><Plus className="h-4 w-4 mr-1" />Add platform</Button>
              </div>
              {platforms.map((p, i) => (
                <div key={i} className="rounded-xl border border-border p-4 space-y-3" data-testid={`platform-block-${i}`}>
                  <div className="flex items-center justify-between gap-3">
                    <Select value={p.platform} onValueChange={(v) => setPlatform(i, 'platform', v)}>
                      <SelectTrigger className="w-48" data-testid={`platform-select-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORM_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                    {platforms.length > 1 && <button type="button" onClick={() => removePlatform(i)} className="text-muted-foreground hover:text-red-600" data-testid={`platform-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><Label className="text-xs">Profile / channel URL</Label><Input value={p.url} onChange={(e) => setPlatform(i, 'url', e.target.value)} placeholder="https://..." data-testid={`platform-url-${i}`} /></div>
                    <div><Label className="text-xs">Handle / username</Label><Input value={p.handle} onChange={(e) => setPlatform(i, 'handle', e.target.value)} placeholder="@handle" data-testid={`platform-handle-${i}`} /></div>
                    <div><Label className="text-xs">Followers / subscribers</Label><Input type="number" value={p.followers} onChange={(e) => setPlatform(i, 'followers', e.target.value)} data-testid={`platform-followers-${i}`} /></div>
                    <div><Label className="text-xs">Average views / reach</Label><Input type="number" value={p.avg_views} onChange={(e) => setPlatform(i, 'avg_views', e.target.value)} data-testid={`platform-views-${i}`} /></div>
                    <div><Label className="text-xs">Engagement rate (%)</Label><Input type="number" step="0.1" value={p.engagement_rate} onChange={(e) => setPlatform(i, 'engagement_rate', e.target.value)} data-testid={`platform-engagement-${i}`} /></div>
                    <div><Label className="text-xs">Content niche</Label><Input value={p.niche} onChange={(e) => setPlatform(i, 'niche', e.target.value)} placeholder="e.g. Budget Asia" data-testid={`platform-niche-${i}`} /></div>
                  </div>
                </div>
              ))}
            </Card>

            {/* D. Audience */}
            <Card className="p-6 rounded-2xl space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><BarChart3 className="h-4 w-4 text-accent" /> Audience</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Primary audience country</Label><Input value={form.audience_primary_country} onChange={(e) => setForm({ ...form, audience_primary_country: e.target.value })} data-testid="apply-audience-country" /></div>
                <div><Label>Secondary countries (comma-separated)</Label><Input value={form.audience_secondary} onChange={(e) => setForm({ ...form, audience_secondary: e.target.value })} /></div>
                <div>
                  <Label>Age range</Label>
                  <Select value={form.age_range} onValueChange={(v) => setForm({ ...form, age_range: v })}>
                    <SelectTrigger data-testid="apply-age-range"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{AGE_RANGES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Budget segment</Label>
                  <Select value={form.budget_segment} onValueChange={(v) => setForm({ ...form, budget_segment: v })}>
                    <SelectTrigger data-testid="apply-budget"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{BUDGET_SEGMENTS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Languages (comma-separated)</Label><Input value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} placeholder="English, Hindi" data-testid="apply-languages" /></div>
                <div><Label>Travel interests (comma-separated)</Label><Input value={form.travel_interests} onChange={(e) => setForm({ ...form, travel_interests: e.target.value })} placeholder="beaches, food, luxury" /></div>
              </div>
            </Card>

            {/* E. Content */}
            <Card className="p-6 rounded-2xl space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><LayoutGrid className="h-4 w-4 text-accent" /> Content</h3>
              <div>
                <Label className="mb-2 block">Content formats</Label>
                <div className="flex flex-wrap gap-2">
                  {CONTENT_FORMATS.map((c) => {
                    const active = form.content_formats.includes(c);
                    return <button type="button" key={c} onClick={() => toggleArr('content_formats', c)} className={`px-3 py-1.5 rounded-full text-sm border ${active ? 'bg-accent text-accent-foreground border-accent' : 'bg-background border-border hover:border-accent/50'}`}>{c}</button>;
                  })}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Travel niches (comma-separated)</Label><Input value={form.niches} onChange={(e) => setForm({ ...form, niches: e.target.value })} placeholder="Luxury, Adventure" data-testid="apply-niches" /></div>
                <div><Label>Destinations covered (comma-separated)</Label><Input value={form.destinations} onChange={(e) => setForm({ ...form, destinations: e.target.value })} placeholder="Japan, Bali" /></div>
                <div><Label>Years creating content</Label><Input type="number" value={form.years_creating} onChange={(e) => setForm({ ...form, years_creating: e.target.value })} /></div>
                <div><Label>Portfolio link</Label><Input value={form.portfolio} onChange={(e) => setForm({ ...form, portfolio: e.target.value })} placeholder="https://..." data-testid="apply-portfolio" /></div>
              </div>
              <div><Label>Previous brand collaborations</Label><Textarea rows={2} value={form.previous_collabs} onChange={(e) => setForm({ ...form, previous_collabs: e.target.value })} /></div>
              <ImageUpload label="Media kit (optional — PDF or image)" kind="document" value={form.media_kit} onChange={(url) => setForm({ ...form, media_kit: url })} testId="media-kit" />
            </Card>

            {/* F. Commercial */}
            <Card className="p-6 rounded-2xl space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><Handshake className="h-4 w-4 text-accent" /> Collaboration interests</h3>
              <p className="text-sm text-muted-foreground">What type of collaboration are you interested in?</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {(options.collab_models || []).map((m) => (
                  <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox checked={form.commercial_models.includes(m)} onCheckedChange={() => toggleArr('commercial_models', m)} data-testid={`collab-${m.split(' ')[0].toLowerCase()}`} />
                    {m}
                  </label>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>Expected fee per itinerary (optional)</Label><Input value={form.expected_fee_itinerary} onChange={(e) => setForm({ ...form, expected_fee_itinerary: e.target.value })} placeholder="e.g. ₹15,000" /></div>
                <div><Label>Expected campaign fee (optional)</Label><Input value={form.expected_campaign_fee} onChange={(e) => setForm({ ...form, expected_campaign_fee: e.target.value })} placeholder="e.g. ₹40,000" /></div>
              </div>
            </Card>

            {/* G. Compliance */}
            <Card className="p-6 rounded-2xl space-y-3">
              <div><Label>Why do you want to join?</Label><Textarea rows={2} value={form.why_join} onChange={(e) => setForm({ ...form, why_join: e.target.value })} data-testid="apply-why" /></div>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={form.accurate} onCheckedChange={(v) => setForm({ ...form, accurate: !!v })} data-testid="apply-accurate" /> I confirm that the information provided is accurate.</label>
              <label className="flex items-start gap-2 text-sm cursor-pointer"><Checkbox checked={form.agree_terms} onCheckedChange={(v) => setForm({ ...form, agree_terms: !!v })} data-testid="apply-agree" /> I agree to the FlightDeals.in creator terms.</label>
            </Card>

            <Button type="submit" disabled={submitting} className="w-full h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90 text-base" data-testid="apply-submit">
              {submitting ? 'Submitting…' : (canResubmit ? 'Resubmit application' : 'Submit application')}
            </Button>
          </form>
        )}
      </section>

      {/* FAQ */}
      <section className="bg-muted/40 border-t border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="font-display text-2xl font-semibold mb-4">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQS.map(([q, a], i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left">{q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </Layout>
  );
}
